import { ESPConstants } from './ESPConstants';
import { Security } from './security/Security';
import { Transport } from './transport/Transport';

/**
 * Session object encapsulates the Transport and Security
 * protocol implementations and is responsible for performing
 * initial handshake with the device to establish a secure session.
 */
export class Session {
    private transport: Transport;
    private security: Security;
    private isSessionEstablished: boolean = false;

    /**
     * Initialize Session object with Transport and Security interface implementations
     *
     * @param transport
     * @param security
     */
    constructor(transport: Transport, security: Security) {
        this.transport = transport;
        this.security = security;
    }

    /**
     * Get the Security implementation object
     *
     * @return Returns the used security type
     */
    public getSecurity(): Security {
        return this.security;
    }

    /**
     * Get the Transport implementation object.
     *
     * @return Returns the used transport type
     */
    public getTransport(): Transport {
        return this.transport;
    }

    /**
     * Get whether a secure Session has been established.
     *
     * @return Returns a boolean true if session is established, false otherwise
     */
    public isEstablished(): boolean {
        return this.isSessionEstablished;
    }

    /**
     * Establish the session by performing handshake with the device
     * based on the Security implementation.
     * Communication with the device will happen over the Transport interface.
     *
     * @return Returns a Promise with void
     */
    public async init(response?: Uint8Array): Promise<void> {
        try {
            const request = this.security.getNextRequestInSession(response);

            if (!request) {
                this.isSessionEstablished = true;
            } else {
                const returnData = await this.transport.sendConfigData(
                    ESPConstants.HANDLER_PROV_SESSION,
                    request
                );

                if (!returnData) {
                    throw new Error('Session could not be established');
                } else {
                    await this.init(returnData);
                }
            }
        } catch (e) {
            if (!response) {
                throw new Error('Session could not be established');
            } else {
                throw e;
            }
        }
    }

    public async sendDataToDevice(
        path: string,
        data: Uint8Array
    ): Promise<Uint8Array> {
        const encryptedData: Uint8Array = this.security.encrypt(data);

        if (this.isSessionEstablished) {
            const returnData = await this.transport.sendConfigData(
                path,
                encryptedData
            );

            const decryptedData: Uint8Array =
                this.security.decrypt(returnData);

            return decryptedData;
        } else {
            await this.init();

            const returnData: Uint8Array = await this.transport.sendConfigData(
                path,
                encryptedData
            );

            return returnData;
        }
    }
}
